import { useState, useEffect } from 'react';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const COUNTRY = 'AU';
const CACHE_VERSION = 3; // bump to bust old caches when filter logic changes

// Calendarific primary_type values that represent an actual day off (national or
// state-specific). Excludes observances, religious/cultural dates, restricted-trading
// days and part-day holidays, which aren't full public holidays.
const DAY_OFF_TYPES = new Set([
    'national holiday',
    'state holiday',
    'common state holiday',
    'state public sector holiday',
    'state bank holiday',
]);

function isDayOffHoliday(holiday) {
    const primaryType = (holiday.primary_type || '').toLowerCase();
    return DAY_OFF_TYPES.has(primaryType);
}

// A holiday's `states` field is either the string "All" (applies everywhere) or an
// array of { abbrev, name, ... } objects for the states it applies to.
function appliesToState(holiday, stateAbbrev) {
    if (holiday.states === 'All') return true;
    if (!stateAbbrev || !Array.isArray(holiday.states)) return false;
    return holiday.states.some(s => (s.abbrev || '').toUpperCase() === stateAbbrev.toUpperCase());
}

function getCacheKey(year) {
    return `holidays_cache_${COUNTRY}_${year}_v${CACHE_VERSION}`;
}

function loadFromLocalStorage(year) {
    try {
        const raw = localStorage.getItem(getCacheKey(year));
        if (!raw) return null;
        const { data, cachedAt } = JSON.parse(raw);
        if (Date.now() - cachedAt > CACHE_TTL_MS) return null; // expired
        return data;
    } catch {
        return null;
    }
}

function saveToLocalStorage(year, data) {
    try {
        localStorage.setItem(getCacheKey(year), JSON.stringify({ data, cachedAt: Date.now() }));
    } catch {
        // localStorage full or unavailable — silently skip
    }
}

async function fetchHolidaysForYear(year) {
    const cached = loadFromLocalStorage(year);
    if (cached) return cached;

    const res = await fetch(`${SERVER_URL}/api/holidays?country=${COUNTRY}&year=${year}`);
    if (!res.ok) throw new Error(`Holiday API error: ${res.status}`);
    const json = await res.json();

    const holidays = json?.response?.holidays || [];
    saveToLocalStorage(year, holidays);
    return holidays;
}

/**
 * Returns a Map<"YYYY-MM-DD", string[]> of public holiday names for quick lookup.
 * Fetches current year + next year so week views near year-end work correctly.
 *
 * @param {string} [stateAbbrev] - the company's state (e.g. "VIC", "QLD"). When provided,
 * state-specific holidays are included only for that state; national holidays always show.
 * When omitted, only national ("All") holidays are included.
 */
export function useHolidays(stateAbbrev) {
    const [holidayMap, setHolidayMap] = useState(new Map());

    useEffect(() => {
        // Clear old cache versions
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('holidays_cache_') && !key.endsWith(`_v${CACHE_VERSION}`)) {
                localStorage.removeItem(key);
            }
        });

        const currentYear = new Date().getFullYear();
        const years = [currentYear, currentYear + 1];

        Promise.all(years.map(y => fetchHolidaysForYear(y).catch(() => [])))
            .then(results => {
                const map = new Map();
                results.flat().forEach(holiday => {
                    const iso = holiday?.date?.iso?.slice(0, 10); // "YYYY-MM-DD"
                    if (!iso) return;

                    if (!isDayOffHoliday(holiday)) return;
                    if (!appliesToState(holiday, stateAbbrev)) return;

                    if (!map.has(iso)) map.set(iso, []);
                    map.get(iso).push(holiday.name);
                });
                setHolidayMap(map);
            });
    }, [stateAbbrev]);

    return holidayMap;
}
